import * as React from 'react';
import type { ImageCDNProvider } from '@packages/common';

import {
  ResponsiveImageConfig,
  BreakpointFlag,
  ResponsiveImageConfigMap,
  ResponsiveImageConfigOverride,
} from './ResponsiveImageTypes';
import { toSrcSet, createConfigsFromFlags, toConfigPairs, toImgSizes, mergeWithConfig } from './ResponsiveImageHelpers';

const responsiveImageConfigMap: ResponsiveImageConfigMap = {
  [BreakpointFlag.XS]: {
    filenameKey: 'XS',
    visibleFrom: 0,
    realWidth: 320,
  },
  [BreakpointFlag.SM]: {
    filenameKey: 'SM',
    visibleFrom: 480,
  },
  [BreakpointFlag.MD]: {
    filenameKey: 'MD',
    visibleFrom: 768,
  },
  [BreakpointFlag.LG]: {
    filenameKey: 'LG',
    visibleFrom: 992,
  },
  [BreakpointFlag.XL]: {
    filenameKey: 'XL',
    visibleFrom: 1250,
  },
  [BreakpointFlag.XXL]: {
    filenameKey: 'XXL',
    visibleFrom: 1550,
  },
};

export const responsiveTileImageConfigMap: ResponsiveImageConfigMap = {
  [BreakpointFlag.XS]: {
    filenameKey: '169x132',
    visibleFrom: 0,
    realWidth: 169,
  },
  [BreakpointFlag.SM]: {
    filenameKey: '277x216',
    visibleFrom: 480,
    realWidth: 277,
  },
  [BreakpointFlag.XL]: {
    filenameKey: '366x275',
    visibleFrom: 1250,
    realWidth: 366,
  },
};

const ImgSource: React.FC<{
  basePath: string;
  extension?: string;
  query?: string;
  min?: boolean;
  mediaWidth: number;
  imageConfig: ResponsiveImageConfig;
  cdnProvider?: ImageCDNProvider;
}> = ({ basePath, imageConfig, extension = 'jpg', min = false, mediaWidth, query, cdnProvider }) => {
  const media = `(${min ? 'min' : 'max'}-width: ${mediaWidth}px)`;
  const srcSet = toSrcSet(basePath, extension, 'auto', query, [imageConfig], cdnProvider);
  return <source srcSet={srcSet} media={media} />;
};

export const ResponsiveImage: React.FC<{
  path: string;
  extension?: string;
  query?: string;
  breakpoints?: BreakpointFlag;
  configOverride?: ResponsiveImageConfigOverride;
  title?: string;
  alt?: string;
  cdnProvider?: ImageCDNProvider;
}> = ({
  path,
  extension = 'jpg',
  breakpoints = BreakpointFlag.All,
  configOverride = {},
  title,
  alt,
  query,
  cdnProvider,
}) => {
  const configMap = mergeWithConfig(responsiveImageConfigMap, configOverride);
  const configs = createConfigsFromFlags(breakpoints, configMap);
  const configPairs = toConfigPairs(configs);
  const imgSrcSet = toSrcSet(path, extension, 'jpg', query, configs, cdnProvider);
  const imgSizes = toImgSizes(...configs);

  return (
    <picture style={{ width: '100%', height: '100%' }}>
      {configPairs.map(([imageConfig, next], idx) => {
        const { visibleFrom } = imageConfig;
        const mediaWidth = next ? next.visibleFrom - 1 : visibleFrom;
        return (
          <ImgSource
            key={idx}
            basePath={path}
            mediaWidth={mediaWidth}
            extension={extension}
            query={query}
            imageConfig={imageConfig}
            min={next ? false : true}
            cdnProvider={cdnProvider}
          />
        );
      })}
      <img
        title={title}
        alt={alt}
        srcSet={imgSrcSet}
        sizes={imgSizes}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </picture>
  );
};
